export interface AssignmentRuleCondition {
  field: string;
  operator: string;
  value: string;
}

export interface AssignmentRuleAction {
  type: "specific" | "round_robin";
  agentId?: string;
  agentName?: string;
}

export interface AssignmentRule {
  id: string;
  name: string;
  active: boolean;
  condition: AssignmentRuleCondition;
  action: AssignmentRuleAction;
  priority: number;
  isFallback?: boolean;
}

export const assignmentRules: AssignmentRule[] = [
  {
    id: "1",
    name: "IndiaMart Leads → Rajesh",
    active: true,
    condition: { field: "Source", operator: "is", value: "IndiaMart" },
    action: { type: "specific", agentId: "1", agentName: "Rajesh Kumar" },
    priority: 1,
  },
  {
    id: "2",
    name: "High Priority → Senior Agent",
    active: true,
    condition: { field: "Priority", operator: "is", value: "High" },
    action: { type: "specific", agentId: "2", agentName: "Priya Sharma" },
    priority: 2,
  },
  {
    id: "3",
    name: "JustDial → Amit",
    active: true,
    condition: { field: "Source", operator: "is", value: "JustDial" },
    action: { type: "specific", agentId: "3", agentName: "Amit Patel" },
    priority: 3,
  },
  {
    id: "4",
    name: "WhatsApp → Sneha",
    active: false,
    condition: { field: "Source", operator: "is", value: "WhatsApp" },
    action: { type: "specific", agentId: "4", agentName: "Sneha Rao" },
    priority: 4,
  },
  {
    id: "5",
    name: "Unassigned fallback",
    active: true,
    condition: { field: "Any", operator: "no_match", value: "fallback" },
    action: { type: "round_robin", agentId: undefined, agentName: undefined },
    priority: 5,
    isFallback: true,
  },
  {
    id: "6",
    name: "IndiaMart Pune → Priya",
    active: true,
    condition: { field: "Source Account", operator: "is", value: "IndiaMart - Pune" },
    action: { type: "specific", agentId: "2", agentName: "Priya Sharma" },
    priority: 6,
  },
  {
    id: "7",
    name: "JustDial Mumbai → Amit",
    active: true,
    condition: { field: "Source Account", operator: "is", value: "JustDial - Mumbai" },
    action: { type: "specific", agentId: "3", agentName: "Amit Patel" },
    priority: 7,
  },
];

export const assignmentMethods = [
  {
    id: "round_robin",
    title: "Round Robin",
    desc: "Leads assigned equally in rotation",
    icon: "ri-refresh-line",
    iconBg: "bg-indigo-100 text-indigo-600",
  },
  {
    id: "load_based",
    title: "Load Based",
    desc: "Assign to agent with fewest open leads",
    icon: "ri-bar-chart-line",
    iconBg: "bg-green-100 text-green-600",
  },
  {
    id: "source_based",
    title: "Source Based",
    desc: "Route by lead source to specific agents",
    icon: "ri-link",
    iconBg: "bg-amber-100 text-amber-600",
  },
  {
    id: "manual_only",
    title: "Manual Only",
    desc: "All leads wait in unassigned queue",
    icon: "ri-user-line",
    iconBg: "bg-purple-100 text-purple-600",
  },
];

export const conditionFields = [
  { value: "Source", label: "Source" },
  { value: "Source Account", label: "Source Account" },
  { value: "Priority", label: "Priority" },
  { value: "Lead Value", label: "Lead Value" },
  { value: "Time of Day", label: "Time of Day" },
  { value: "Stage", label: "Stage" },
];

export const conditionOperators = [
  { value: "is", label: "is" },
  { value: "is not", label: "is not" },
];

export const conditionValueOptions: Record<string, string[]> = {
  Source: ["IndiaMart", "JustDial", "WhatsApp", "Website", "TradeIndia", "Social Media"],
  "Source Account": [
    "IndiaMart - Mumbai",
    "IndiaMart - Pune",
    "JustDial - Mumbai",
    "JustDial - Pune",
    "TradeIndia - Main",
    "WhatsApp - Sales",
    "Website Forms - Main",
    "Social Media - Main",
  ],
  Priority: ["High", "Medium", "Low"],
  Industry: ["Real Estate", "Construction", "SaaS", "Manufacturing", "Retail", "Education"],
  "Lead Value": ["High", "Medium", "Low"],
  "Time of Day": ["Morning (6AM-12PM)", "Afternoon (12PM-5PM)", "Evening (5PM-10PM)"],
  Stage: [
    "New Inquiry",
    "Site Visit Scheduled",
    "Site Visit Done",
    "Proposal Sent",
    "Negotiation",
    "Booking Done",
    "Lost",
  ],
};

export const agents = [
  { id: "1", name: "Rajesh Kumar", role: "Super Admin", initialized: "RK" },
  { id: "2", name: "Priya Sharma", role: "Sales Manager", initialized: "PS" },
  { id: "3", name: "Amit Patel", role: "Sales Executive", initialized: "AP" },
  { id: "4", name: "Sneha Rao", role: "Viewer", initialized: "SR" },
];

export interface AgentWorkload {
  id: string;
  name: string;
  role: string;
  initialized: string;
  openLeads: number;
}

export const agentWorkloads: AgentWorkload[] = [
  { id: "1", name: "Rajesh Kumar", role: "Super Admin", initialized: "RK", openLeads: 34 },
  { id: "2", name: "Priya Sharma", role: "Sales Manager", initialized: "PS", openLeads: 28 },
  { id: "3", name: "Amit Patel", role: "Sales Executive", initialized: "AP", openLeads: 19 },
  { id: "4", name: "Sneha Rao", role: "Viewer", initialized: "SR", openLeads: 8 },
];

export interface UnassignedLead {
  id: string;
  name: string;
  source: string;
  sourceAccount?: string;
  stage: string;
  waitTime: string;
  value: string;
}

export const unassignedLeads: UnassignedLead[] = [
  { id: "ul-1", name: "Pooja Menon", source: "Social Media", stage: "New Inquiry", waitTime: "3 hrs", value: "₹5,500" },
  { id: "ul-2", name: "Arjun Kapoor", source: "JustDial", sourceAccount: "Pune", stage: "New Inquiry", waitTime: "5 hrs", value: "₹7,200" },
  { id: "ul-3", name: "Meera Kulkarni", source: "Website", stage: "New Inquiry", waitTime: "1 day", value: "₹78,500" },
  { id: "ul-4", name: "Gaurav Saxena", source: "TradeIndia", stage: "Proposal Sent", waitTime: "12 hrs", value: "₹41,000" },
  { id: "ul-5", name: "Rahul Verma", source: "WhatsApp", stage: "New Inquiry", waitTime: "30 mins", value: "₹31,000" },
  { id: "ul-6", name: "Manoj Tiwari", source: "Website", stage: "Proposal Sent", waitTime: "1 hr", value: "₹52,000" },
];

export interface EscalationRule {
  id: string;
  name: string;
  trigger: string;
  action: string;
  active: boolean;
}

export const escalationRules: EscalationRule[] = [
  { id: "es-1", name: "Uncontacted > 2 hours", trigger: "Lead not contacted within 2 hours of assignment", action: "Notify Team Lead via email", active: true },
  { id: "es-2", name: "Stuck in stage > 7 days", trigger: "Lead stays in same stage for more than 7 days", action: "Reassign to senior agent", active: true },
  { id: "es-3", name: "High value > 4 hrs unassigned", trigger: "Lead value > ₹50,000 & unassigned > 4 hours", action: "Alert Admin + Auto-assign to top performer", active: false },
];

export interface AssignmentLogEntry {
  id: string;
  lead: string;
  assignedTo: string;
  rule: string | null;
  method: string;
  timestamp: string;
  success: boolean;
}

export const assignmentLog: AssignmentLogEntry[] = [
  { id: "log-1", lead: "Vikram Mehta", assignedTo: "Rajesh Kumar", rule: "IndiaMart Leads → Rajesh", method: "Rule", timestamp: "2 mins ago", success: true },
  { id: "log-2", lead: "Deepika Singh", assignedTo: "Priya Sharma", rule: "IndiaMart Pune → Priya", method: "Rule", timestamp: "5 mins ago", success: true },
  { id: "log-3", lead: "Sunita Rao", assignedTo: "Rajesh Kumar", rule: "JustDial Mumbai → Amit", method: "Rule", timestamp: "8 mins ago", success: true },
  { id: "log-4", lead: "Suresh Nair", assignedTo: "Priya Sharma", rule: null, method: "Round Robin", timestamp: "12 mins ago", success: true },
  { id: "log-5", lead: "Kavita Joshi", assignedTo: "Amit Patel", rule: null, method: "Manual", timestamp: "18 mins ago", success: true },
  { id: "log-6", lead: "Rohit Bansal", assignedTo: "Rajesh Kumar", rule: "IndiaMart Leads → Rajesh", method: "Rule", timestamp: "25 mins ago", success: true },
  { id: "log-7", lead: "Anita Desai", assignedTo: "Priya Sharma", rule: "High Priority → Senior Agent", method: "Rule", timestamp: "32 mins ago", success: true },
  { id: "log-8", lead: "Lakshmi Iyer", assignedTo: "Sneha Rao", rule: null, method: "Load Based", timestamp: "40 mins ago", success: true },
];